import { Dropdown, Icon, Input } from 'Components';
import Carousel from 'LearningManager/components/Carousel';
import CourseCard from 'LearningManager/components/CourseCard';
import CourseDetailModal from 'LearningManager/components/CourseDetailModal';
import Heading from 'LearningManager/components/Heading';
import {
	IndividualCoursesData,
	courseDetailMockData
} from 'LearningManager/constants/createAssignment.constant';
import styles from 'LearningManager/styles/CustomSetup.module.scss';
import { Menu } from 'antd';
import { useState } from 'react';
import { useStepper } from 'LearningManager/hooks/Stepper.context';
import { useQuery } from 'LearningManager/pages/CreateAssignment';

const SelectCourses: React.FC = () => {
	const [showDetails, setShowDetails] = useState(false);
	const query = useQuery();
	const isEditMode = Boolean(query.get('assignmentId'));
	return (
		<div id="lms-ca-select-courses" className={styles.root}>
			<Heading
				title={isEditMode ? 'Edit Courses' : 'Select Courses'}
				subHeading="Select courses or learning paths to edit"
			/>
			<Filters />
			<LearningPath openDetails={() => setShowDetails(true)} />
			<IndividualCourses openDetails={() => setShowDetails(true)} />
			<CourseDetailModal
				visible={showDetails}
				onClose={() => setShowDetails(false)}
				{...courseDetailMockData}
			/>
		</div>
	);
};

const Filters = () => {
	const menu = (
		<Menu style={{ borderRadius: '6px' }}>
			<Menu.Item>English</Menu.Item>
			<Menu.Item disabled>Spanish</Menu.Item>
			<Menu.Item disabled>French</Menu.Item>
			<Menu.Item disabled>German</Menu.Item>
		</Menu>
	);

	return (
		<div id="lms-ca-select-courses-filters" className={styles.filter}>
			<Input.Search
				id="lms-ca-select-courses-search"
				className={styles.input}
				placeholder="Search"
				suffix={<Icon type="search" role="img" />}
			/>
			<div id="lms-ca-select-courses-language" className={styles.select}>
				<Dropdown overlay={menu} className={styles.dropdown}>
					English
				</Dropdown>
			</div>
		</div>
	);
};

const LearningPath = ({ openDetails }: { openDetails: () => void }) => {
	const {
		draft: {
			customSetup: { selectedLearningPathCourseIds }
		},
		toggleCustomCourseSelection
	} = useStepper();
	const courses = [...IndividualCoursesData[0].courses];

	const toggleSelect = (id: number) => {
		toggleCustomCourseSelection('selectedLearningPathCourseIds', id);
	};

	return (
		<div
			id="lms-ca-select-courses-learning-paths"
			className={styles.learningWrapper}
		>
			<Heading
				icon={<Icon type="pathIcon" role="img" />}
				title="Learning Paths"
				subHeading="A structured series of courses for effective cybersecurity learning"
			/>
			<Carousel
				data={courses}
				label="Data Privacy Training"
				itemsPerPage={4}
				renderItem={(course) => (
					<CourseCard
						index={course.id}
						htmlId={`lms-ca-lp-course-${course.id}`}
						course={{
							...course,
							isSelected: selectedLearningPathCourseIds.includes(
								course.id
							)
						}}
						onClick={openDetails}
						onSelect={toggleSelect}
					/>
				)}
			/>
		</div>
	);
};

const IndividualCourses = ({ openDetails }: { openDetails: () => void }) => {
	const {
		draft: {
			customSetup: { selectedIndividualCourseIds }
		},
		toggleCustomCourseSelection
	} = useStepper();
	const courses = [...IndividualCoursesData[0].courses];

	const toggleSelect = (id: number) => {
		toggleCustomCourseSelection('selectedIndividualCourseIds', id);
	};

	const groups: { scope: string; label: string; data: typeof courses }[] = [
		{ scope: 'dp1', label: 'Data Privacy', data: courses },
		{ scope: 'dp2', label: 'Data Privacy', data: courses },
		{ scope: 'pt', label: 'Privacy', data: courses },
		{ scope: 'hr', label: 'Human Resources', data: courses.slice(0, 2) },
		{ scope: 'other', label: 'Other Topics', data: courses }
	];

	return (
		<div
			id="lms-ca-select-courses-individual"
			className={styles.learningWrapper}
		>
			<Heading
				icon={<Icon type="indiCourses" role="img" />}
				title="Individual Courses"
			/>
			<div className={styles.individualWrapper}>
				{groups.map((group) => (
					<div
						key={group.scope}
						id={`lms-ca-ind-carousel-${group.scope}`}
					>
						<Carousel
							data={group.data}
							label={group.label}
							itemsPerPage={4}
							renderItem={(course) => (
								<CourseCard
									index={course.id}
									htmlId={`lms-ca-ind-${group.scope}-course-${course.id}`}
									course={{
										...course,
										isSelected: selectedIndividualCourseIds.includes(
											course.id
										)
									}}
									onClick={openDetails}
									onSelect={toggleSelect}
								/>
							)}
						/>
					</div>
				))}
			</div>
		</div>
	);
};

export default SelectCourses;
